import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

/** Annahmen der Rechnung, wie der Server sie liefert und annimmt (`PV_DEFAULTS` in tri-server). */
export interface PvAssumptions {
  kWpPerM2: number;
  performanceRatio: number;
  usableShareSloped: number;
  usableShareFlat: number;
  flatRoofFactor: number;
  minUsableArea: number;
  minFactor: number;
  maxTilt: number;
  electricityPrice: number;
  selfConsumptionShare: number;
  feedInTariff: number;
  operatingCostShare: number;
}

export interface PvSurface {
  orientation: string;
  tilt: number;
  azimuth: number | null;
  area: number;
  usableArea: number;
  factor: number;
  kWp: number;
  yieldKWh: number;
  excluded: string | null;
}

export interface PvPotential {
  buildingId: string;
  address: string | null;
  available: boolean;
  reason?: string;
  assumptions: PvAssumptions;
  roofType?: string | null;
  radiation?: { value: number; period: string; unit: string } | null;
  surfaces?: PvSurface[];
  totals?: { roofArea: number; usableArea: number; kWp: number; yieldKWh: number; specificYield: number | null };
  optimumYieldPerKWp?: number;
  economics?: { investment: number; savingsPerYear: number; paybackYears: number | null };
  costPerKWp?: number;
  existing?: { units: { mastrNumber: string; name: string | null; kWp: number | null; commissioned: string | null }[]; kWp: number; note: string };
  sources?: string[];
  caveat?: string;
}

export const fetchPvPotential = async (building: string, assumptions: Partial<PvAssumptions> = {}): Promise<PvPotential> => {
  const query = new URLSearchParams(Object.entries(assumptions).map(([k, v]) => [k, String(v)])).toString();
  const response = await authenticatedFetch(
    `${configuration.serverUrl}/buildings/${encodeURIComponent(building)}/pv-potential${query ? `?${query}` : ""}`,
    { headers: { "Content-Type": "application/json" } },
  );
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // kein JSON
  }
  throw new ApplicationError(message);
};

/** Ersteinschätzung des Energiebedarfs (tri-server `energyDemand.ts`). */
export interface EnergyDemand {
  buildingId: string;
  address: string | null;
  available: boolean;
  reason?: string;
  year?: number;
  yearSource?: string;
  fuel?: string;
  fuelSource?: string;
  renovation?: "typisch" | "unsaniert";
  usableAreaM2?: number;
  sharedWallShare?: number;
  sharedWallSource?: string;
  heatingKWh?: number;
  hotWaterKWh?: number;
  finalEnergyKWh?: number;
  finalEnergyPerM2?: number;
  efficiencyClass?: string;
  co2Kg?: number;
  co2KgPerM2?: number;
  unrenovated?: { finalEnergyPerM2: number; efficiencyClass: string; co2KgPerM2: number };
  sources?: string[];
  caveat?: string;
}

export const fetchEnergyDemand = async (
  building: string,
  options: { year?: number; fuel?: string; renovation?: "typisch" | "unsaniert" } = {},
): Promise<EnergyDemand> => {
  const query = new URLSearchParams(
    Object.entries(options).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]),
  ).toString();
  const response = await authenticatedFetch(
    `${configuration.serverUrl}/buildings/${encodeURIComponent(building)}/energy-demand${query ? `?${query}` : ""}`,
    { headers: { "Content-Type": "application/json" } },
  );
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  throw new ApplicationError(`${response.status} ${response.statusText}`);
};

/** Verbrauch aus der erfassten Technik (tri-server `tgaConsumption.ts`). */
export interface TgaConsumption {
  buildingId: string;
  assets: { assetId: string; name: string | null; code: string | null; group: string | null; medium: string | null; powerKW: number | null; powerSource: string | null; hours: number | null; kWh: number; method: string; accuracy: 1 | 2; technical: boolean }[];
  counted: number;
  technical: number;
  typical: number;
  heatFromModel: boolean;
  withoutPower: number;
  totals: { electricityKWh: number; fuelKWh: number; kWh: number; co2Kg: number };
  areaM2: number | null;
  areaSource: string | null;
  intensity: { kWhPerM2: number; co2KgPerM2: number } | null;
  factors: { electricity: number; fuel: number; fuelLabel: string };
  caveat: string;
}

const getJson = async (path: string) => {
  const response = await authenticatedFetch(`${configuration.serverUrl}${path}`, { headers: { "Content-Type": "application/json" } });
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  throw new ApplicationError(`${response.status} ${response.statusText}`);
};

export const fetchTgaConsumption = (building: string): Promise<TgaConsumption> =>
  getJson(`/buildings/${encodeURIComponent(building)}/tga-consumption`);

/** Dekarbonisierungspfad (tri-server `decarbonisation.ts`). */
export interface DecarbMeasure {
  key: "envelope" | "heatPump" | "pv";
  label: string;
  investment: number;
  subsidy: number;
  deltaElectricityKWh: number;
  deltaFuelKWh: number;
  savingsPerYear: number;
  detail: string;
  assessment?: { lifetimeYears: number; paybackYears: number | null; co2SavedTPerYear: number; abatementCostPerT: number | null; co2Share: number; verdict: 'lohnt sich' | 'lohnt sich nicht' | 'wenig Wirkung'; reason: string };
}

export interface Decarbonisation {
  available: boolean;
  reason?: string;
  baseline?: { stage: string; source: string; areaM2: number; electricityKWh: number; fuelKWh: number; fuelLabel: string };
  reference?: { value: number; year: number; source: string; note: string };
  measures?: DecarbMeasure[];
  chosen?: { key: DecarbMeasure["key"]; startYear: number }[];
  path?: { year: number; target: number; baseline: number; withMeasures: number }[];
  stranding?: { baseline: number | null; withMeasures: number | null };
  onTrackFrom?: { baseline: number | null; withMeasures: number | null };
  residual2045?: { baseline: number; withMeasures: number };
  co2Cost2045?: { baseline: number; withMeasures: number };
  investment?: number;
  sources?: string[];
  caveat?: string;
}

export const fetchDecarbonisation = (building: string, measures: Partial<Record<DecarbMeasure["key"], number>>): Promise<Decarbonisation> => {
  const query = new URLSearchParams(Object.entries(measures).map(([k, v]) => [k, String(v)])).toString();
  return getJson(`/buildings/${encodeURIComponent(building)}/decarbonisation${query ? `?${query}` : ""}`);
};
